import React, { useReducer, useEffect, forwardRef, useRef } from "react";
import { default as ReactModal } from "react-modal";
import { isMobile } from "react-device-detect";
import { Pagination } from "swiper";
import { Swiper, SwiperSlide } from "components/swiper";
import Button from "components/button";
import { ReactComponent as Play } from "assets/svgs/play.svg";
import { ReactComponent as SoundOff } from "assets/svgs/sound-off.svg";
import { ReactComponent as SoundOn } from "assets/svgs/sound-on.svg";
import { ReactComponent as Close } from "assets/svgs/close.svg";

const settings = {
	modules: [Pagination],
	direction: "vertical",
	pagination: {
		clickable: true,
	},
};

const initialState = {
	isVideoPlaying: {},
	videoDurations: {},
	isMuted: false,
	activeIndex: 0,
};

const reducer = (state, action) => {
	switch (action.type) {
		case "SET_IS_VIDEO_PLAYING":
			return {
				...state,
				isVideoPlaying: { ...state.isVideoPlaying, [action.key]: action.value },
			};
		case "SET_VIDEO_DURATIONS":
			return {
				...state,
				videoDurations: { ...state.videoDurations, [action.key]: action.value },
			};
		case "SET_IS_MUTED":
			return { ...state, isMuted: action.value };
		case "SET_ACTIVE_INDEX":
			return { ...state, activeIndex: action.value };
		default:
			return state;
	}
};

const FullScreenSlider = forwardRef(({ sliderData, startFrom, setIsFullScreen, cta, areMobileVideosMuted,
	setUnmutedVideoIndex, setAreDesktopVideosMuted }, ref) => {
	const [state, dispatch] = useReducer(reducer, { ...initialState, isMuted: areMobileVideosMuted, activeIndex: startFrom });
	const videoRefs = useRef([]);

	if (videoRefs.current.length !== sliderData.length) {
		videoRefs.current = Array(sliderData.length).fill().map((_, i) => videoRefs.current[i] || React.createRef());
	}

	const formatDuration = (duration) => {
		if (duration >= 60) {
			return { minutes: Math.floor(duration / 60), seconds: Math.floor(duration % 60) };
		} else return Math.ceil(duration);
	};

	const handleSlideChange = (swiper) => {
		const newIndex = swiper.activeIndex;
		dispatch({ type: "SET_ACTIVE_INDEX", value: newIndex });
		sliderData.forEach((_, index) => {
			const video = videoRefs.current[index].current;
			if (video) {
				if (index !== newIndex) {
					video.pause();
					video.currentTime = 0;
				} else {
					video.play();
				}
			}
		});

		const nextIndex = newIndex + 1;
		if (nextIndex < sliderData.length) {
			const nextVideo = videoRefs.current[nextIndex].current;
			if (nextVideo) {
				if (nextVideo.readyState < 3) {
					nextVideo.load();
				}
			}
		}
	};

	const formatSeconds = (seconds) => {
		return seconds > 9 ? seconds : `0${seconds}`;
	};

	const handleVideoState = (key, isPlaying) => {
		dispatch({ type: "SET_IS_VIDEO_PLAYING", key, value: isPlaying });
	};

	const handleTimeUpdate = (e, key) => {
		const currentTime = e.target.currentTime;
		dispatch({ type: "SET_IS_VIDEO_PLAYING", key, value: currentTime });
	};

	const togglePlayPause = (index) => {
		const video = videoRefs.current[index].current;
		if (video) {
			if (video.paused) {
				video.play();
			} else {
				video.pause();
			}
		}
	};

	const handlePauseAndReset = (i) => {
		sliderData.forEach((_, index) => {
			const video = videoRefs.current[index].current;
			if (video) {
				video.pause();
				video.currentTime = 0;
			}
		});
		if (isMobile) {
			if (state.isMuted) {
				setAreDesktopVideosMuted(true);
				setUnmutedVideoIndex(-1);
			} else {
				setAreDesktopVideosMuted(false);
				setUnmutedVideoIndex(i);
			}
			setIsFullScreen(false);
		}
	};

	useEffect(() => {
		const video = videoRefs.current[startFrom].current;
		if (video) {
			video.play();
		}
	}, [startFrom]);

	useEffect(() => {
		if (ref.current && ref.current.swiper) {
			handleSlideChange(ref.current.swiper);
		}
	}, [state.activeIndex]);

	return (
		<ReactModal
			isOpen={true}
			onRequestClose={handlePauseAndReset}
			className="relative w-[100%] h-[100%]"
			style={{ overlay: { zIndex: 99999 } }}
			appElement={document.body}
			ref={ref}
		>
			<Swiper className="m-auto h-full"
				{...settings} initialSlide={startFrom}
				onSlideChange={handleSlideChange}
			>
				{sliderData.map((slide, index) => (
					<SwiperSlide className="m-auto" key={index}>
						<video
							id={`video-${index}`}
							autoPlay={index === startFrom}
							loop
							playsInline
							ref={videoRefs.current[index]}
							muted={state.isMuted || slide.video.muted}
							poster={slide.thumbnail}
							onLoadedMetadata={(e) => {
								const duration = e.target.duration;
								dispatch({ type: "SET_VIDEO_DURATIONS", key: index, value: formatDuration(duration) });
							}}
							onPlay={() => handleVideoState(index, true)}
							onPause={() => handleVideoState(index, false)}
							onClick={() => togglePlayPause(index)}
							onTimeUpdate={(e) => handleTimeUpdate(e, index)}
							className="rounded-[12px] absolute top-0 right-0 h-screen w-[100%] h-[100%] object-cover"
						>
							<source src={slide.video.src} type="video/mp4" />
							Your browser does not support HTML5 video.
						</video>
						<Button data={cta} className="btn-center absolute bottom-10 left-[50%] transform -translate-x-[50%]" />
						{state.videoDurations[index] && (
							<div className="absolute right-5 bottom-5 flex">
								{state.isVideoPlaying[index] ? (
									<>
										<h6>{formatSeconds(Math.floor(state.isVideoPlaying[index] / 60))}</h6>
										<h6>:</h6>
										<h6>{formatSeconds(Math.floor(state.isVideoPlaying[index] % 60))}</h6>
									</>
								) : (
									typeof state.videoDurations[index] === "object" ? (
										<>
											<h6>{state.videoDurations[index].minutes}</h6>
											<h6>:</h6>
											<h6>{state.videoDurations[index].seconds}</h6>
										</>
									) : (
										<>
											<h6>00</h6>
											<h6>:</h6>
											<h6>{formatSeconds(state.videoDurations[index])}</h6>
										</>
									)
								)}
							</div>
						)}
						{state.isVideoPlaying[index] ? (
							<div className="video-pause-reset-btn" >
								<button
									onClick={() => handlePauseAndReset(index)}
									className="rounded-full p-3 bg-white bg-opacity-30 mb-2 cursor-pointer"
								>
									<Close width="22" height="22" fill="white" />
								</button>
								{!slide.video.muted ?
									<button
										onClick={() => dispatch({ type: "SET_IS_MUTED", value: !state.isMuted })}
										className="rounded-full bg-white bg-opacity-30 p-3 cursor-pointer"
									>
										{state.isMuted ? (
											<SoundOff width="22" height="22" />
										) : (
											<SoundOn width="22" height="22" />
										)}
									</button>
									: <></>}
							</div>
						) : (
							<button onClick={() => togglePlayPause(index)} className="video-play-pause-btn">
								<Play />
							</button>
						)}
					</SwiperSlide>
				))}
			</Swiper >
		</ReactModal >
	);
});

FullScreenSlider.displayName = "FullScreenSlider";
export default FullScreenSlider;
